import { db, storage } from "firebase.js";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteField,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

// 🔹 Fetch all locations, without the discarded ones unless asked
// (the admin dashboard needs them to keep their ids taken and label old admin rights).
export const getAllLocations = async ({ includeDiscarded = false } = {}) => {
  const snapshot = await getDocs(collection(db, "locations"));

  console.log(`Firestore read count: ${snapshot.docs.length}`);

  return snapshot.docs
    .map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }))
    .filter((loc) => includeDiscarded || !loc.discarded);
};

export const fetchLocationById = async (locationId) => {
  const docRef = doc(db, "locations", locationId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) return null;

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
};

export const getDisplayRanking = async (locationId) => {
  const ref = doc(db, "locations", locationId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) throw new Error("Location not found");
  return snapshot.data().displayRanking || false;
};

export const updateDisplayRanking = async (locationId, value) => {
  const docRef = doc(db, "locations", locationId);
  await updateDoc(docRef, { displayRanking: value });
};

export const uploadLocationImage = async (locationId, file) => {
  const storageRef = ref(storage, `locations/${locationId}/image`);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  const docRef = doc(db, "locations", locationId);
  await updateDoc(docRef, { image: url });
  return url;
};

export const updateLocation = async (locationId, data) => {
  const docRef = doc(db, "locations", locationId);
  await updateDoc(docRef, data);
};

export const deleteLocationImage = async (locationId, imageUrl) => {
  const storageRef = ref(storage, imageUrl);
  await deleteObject(storageRef);
  const docRef = doc(db, "locations", locationId);
  await updateDoc(docRef, { image: deleteField() });
};

// 🔹 Location ids are URL slugs ("tapis-vert-leonhart"): they appear in /locations/:id,
// in tournament ids and in the storage path, so they are chosen once at creation.
export const LOCATION_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const slugifyLocationId = (text = "") =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// 🔹 Create (master only). Fails if the id is already taken: a plain setDoc would
// silently overwrite the existing location, since masters may also update.
export const createLocation = async (locationId, data) => {
  const docRef = doc(db, "locations", locationId);
  await runTransaction(db, async (tx) => {
    const existing = await tx.get(docRef);
    if (existing.exists()) {
      const err = new Error(`Location "${locationId}" already exists`);
      err.code = "location/already-exists";
      throw err;
    }
    tx.set(docRef, data);
  });
};

// 🔹 "Delete" (master only) is a soft delete: the location is flagged and dated, and
// everything stays in the database (document, image, tournaments, rankings, admins).
// Discarded locations are left out of getAllLocations, so they leave every list.
export const discardLocation = async (locationId, userId) => {
  await updateDoc(doc(db, "locations", locationId), {
    discarded: true,
    discardedAt: serverTimestamp(),
    discardedBy: userId || null,
  });
};
